import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, getAuthHeaders } from "../context/AuthContext";
import { Calendar, Download, IdCard, Receipt, TrendingDown } from "lucide-react";
import { toast } from "sonner";

/**
 * Admin TDS Report — Section 194H compliance.
 * Shows aggregated TDS deducted between two dates, month-wise breakdown,
 * and a CSV download for filing quarterly Form 26Q.
 */
export default function TDSReport() {
  // Default range: current financial year (Apr 1 → today)
  const today = new Date();
  const fyStartYear = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
  const [from, setFrom] = useState(`${fyStartYear}-04-01`);
  const [to, setTo] = useState(today.toISOString().slice(0, 10));
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/tds/summary`, {
        params: { from, to },
        headers: getAuthHeaders(),
      });
      setSummary(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to load TDS summary");
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => { load(); }, [load]);

  const download = () => {
    const url = `${API}/admin/tds/export.csv?from=${from}&to=${to}`;
    // Include auth header via fetch → blob workaround (browsers don't send custom headers on direct link)
    fetch(url, { headers: getAuthHeaders() })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.blob();
      })
      .then((blob) => {
        const a = document.createElement("a");
        const objUrl = URL.createObjectURL(blob);
        a.href = objUrl;
        a.download = `tds_report_${from}_to_${to}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(objUrl);
        toast.success("TDS report downloaded");
      })
      .catch((err) => toast.error(`Download failed: ${err.message}`));
  };

  return (
    <div className="space-y-6" data-testid="tds-report-section">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <IdCard size={20} className="text-[#00E5FF]" />
            <h2 className="text-xl font-black text-white tracking-tight" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              TDS Report · Section 194H
            </h2>
          </div>
          <p className="text-xs text-[#A1A1AA]">
            Aggregated TDS deducted on affiliate withdrawals. Download the CSV to file Form 26Q returns.
          </p>
        </div>
      </div>

      {/* Date range + export */}
      <div className="p-4 rounded-lg border border-white/10 bg-[#121212]/60 flex flex-col md:flex-row items-end gap-3">
        <div className="flex-1 grid grid-cols-2 gap-3 w-full">
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-[#A1A1AA] mb-1 flex items-center gap-1">
              <Calendar size={11} /> From (IST)
            </label>
            <input
              type="date"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              data-testid="tds-from-date"
              className="w-full bg-[#0A0A0A] border border-white/10 rounded-md px-3 py-2 text-sm text-white font-mono"
            />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-[#A1A1AA] mb-1 flex items-center gap-1">
              <Calendar size={11} /> To (IST)
            </label>
            <input
              type="date"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              data-testid="tds-to-date"
              className="w-full bg-[#0A0A0A] border border-white/10 rounded-md px-3 py-2 text-sm text-white font-mono"
            />
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={load}
            disabled={loading}
            data-testid="tds-refresh-btn"
            className="px-4 py-2 rounded-md text-xs font-bold border border-white/10 text-[#A1A1AA] hover:text-white hover:border-white/25 disabled:opacity-50"
          >
            {loading ? "Loading…" : "Refresh"}
          </button>
          <button
            onClick={download}
            data-testid="tds-download-csv-btn"
            className="px-4 py-2 rounded-md text-xs font-bold bg-[#00E5FF] text-black hover:bg-[#00b4d8] flex items-center gap-1.5"
          >
            <Download size={12} /> Export CSV
          </button>
        </div>
      </div>

      {/* KPI tiles */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="tds-kpi-row">
          <div className="p-4 rounded-lg border border-emerald-500/30 bg-emerald-500/5">
            <div className="flex items-center gap-2 text-emerald-300 text-[10px] font-bold uppercase tracking-wider mb-2">
              <TrendingDown size={12} /> Total TDS
            </div>
            <p className="text-2xl font-black text-white font-mono" data-testid="tds-total">₹{Number(summary.total_tds).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</p>
            <p className="text-[10px] text-[#A1A1AA] mt-1">to be filed to Income Tax</p>
          </div>
          <div className="p-4 rounded-lg border border-white/10 bg-[#0A0A0A]">
            <div className="text-[10px] font-bold uppercase tracking-wider text-[#A1A1AA] mb-2">Gross Withdrawn</div>
            <p className="text-lg font-black text-white font-mono">₹{Number(summary.total_gross).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</p>
            <p className="text-[10px] text-[#A1A1AA] mt-1">{summary.count} withdrawals</p>
          </div>
          <div className="p-4 rounded-lg border border-white/10 bg-[#0A0A0A]">
            <div className="text-[10px] font-bold uppercase tracking-wider text-[#A1A1AA] mb-2">GST 18% Collected</div>
            <p className="text-lg font-black text-white font-mono">₹{Number(summary.total_gst).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</p>
          </div>
          <div className="p-4 rounded-lg border border-white/10 bg-[#0A0A0A]">
            <div className="text-[10px] font-bold uppercase tracking-wider text-[#A1A1AA] mb-2">Net Paid Out</div>
            <p className="text-lg font-black text-white font-mono">₹{Number(summary.total_net_payout).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</p>
          </div>
        </div>
      )}

      {/* Month breakdown */}
      {summary && summary.by_month?.length > 0 && (
        <div className="rounded-lg border border-white/10 bg-[#121212]/60 overflow-hidden">
          <div className="px-4 py-3 border-b border-white/10 flex items-center gap-2">
            <Receipt size={14} className="text-[#00E5FF]" />
            <h3 className="text-sm font-bold text-white">Month-wise TDS</h3>
          </div>
          <table className="w-full text-xs" data-testid="tds-monthly-table">
            <thead className="bg-white/[0.02]">
              <tr className="text-[10px] uppercase tracking-wider text-[#A1A1AA]">
                <th className="text-left px-4 py-2 font-medium">Month (IST)</th>
                <th className="text-right px-4 py-2 font-medium">Withdrawals</th>
                <th className="text-right px-4 py-2 font-medium">Gross ₹</th>
                <th className="text-right px-4 py-2 font-medium">TDS ₹</th>
              </tr>
            </thead>
            <tbody>
              {summary.by_month.map((m) => (
                <tr key={m.month} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`tds-row-${m.month}`}>
                  <td className="px-4 py-2 font-mono text-white">{m.month}</td>
                  <td className="text-right px-4 py-2 text-[#A1A1AA]">{m.count}</td>
                  <td className="text-right px-4 py-2 font-mono text-white">₹{Number(m.gross).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                  <td className="text-right px-4 py-2 font-mono text-emerald-300">₹{Number(m.tds).toLocaleString("en-IN", { minimumFractionDigits: 2 })}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {summary && summary.count === 0 && (
        <div className="p-6 rounded-lg border border-white/10 bg-[#121212]/60 text-center" data-testid="tds-empty-state">
          <IdCard size={32} className="text-[#A1A1AA] mx-auto mb-2" />
          <p className="text-sm text-white font-medium">No TDS deducted in this range</p>
          <p className="text-xs text-[#A1A1AA] mt-1">TDS starts applying once an affiliate crosses ₹20,000 gross withdrawals in a calendar month.</p>
        </div>
      )}
    </div>
  );
}
